import { Badge } from '@/global/components/ui/badge'
import { Button } from '@/global/components/ui/button'
import { Popover, PopoverContent } from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import {
    Building2Icon,
    FolderTreeIcon,
    MapPinIcon,
    RotateCcwIcon,
    UsersIcon,
} from 'lucide-react'
import React from 'react'
import { ShellContext, useEscopoContext } from './context'
import { arr, FACETAS, type FacetaId, LABEL } from './escopo'

/** Altura fixa do painel: evita o popover crescer/encolher ao trocar de faceta. */
const ALTURA_PAINEL = 'h-[300px]'

/** Ícone por faceta — compartilhado com os badges do `Trigger`. */
export const ICONES_FACETA: Record<
    FacetaId,
    React.ComponentType<{ className?: string }>
> = {
    empresas: Building2Icon,
    centrosCusto: FolderTreeIcon,
    locais: MapPinIcon,
    funcionarios: UsersIcon,
}

/**
 * Shell popover: rail de facetas com contadores + painel da faceta ativa +
 * rodapé com a contagem do escopo. As partes ficam montadas fora do popover
 * para que o registro (ordem e itens do rail) não se perca ao fechar.
 */
export function Cascata({ children }: { children: React.ReactNode }) {
    const {
        registro,
        ativa,
        setAtiva,
        valor,
        dados,
        limpar,
        aberto,
        setAberto,
        setPainel,
        toggleDemitidos,
    } = useEscopoContext()

    // conta as quatro facetas, não só as do rail: uma faceta escondida por
    // agrupamento continua filtrando, e limpar tem que ficar disponível
    const totalSelecionado = FACETAS.reduce(
        (total, id) => total + arr(valor[id]).length,
        0,
    )
    const temFuncionarios = registro.some(({ id }) => id === 'funcionarios')

    return (
        <ShellContext.Provider value="cascata">
            <Popover open={aberto} onOpenChange={setAberto}>
                {children}
                <PopoverContent
                    align="start"
                    sideOffset={2}
                    arrow={false}
                    onFocusOutside={e => e.preventDefault()}
                    className={cn(
                        'w-[min(38rem,calc(100vw-2rem))] max-w-none',
                        'overflow-hidden rounded-xl p-0 shadow-xl border border-border/80 bg-popover',
                    )}
                >
                    <div className="flex max-sm:flex-col">
                        <nav
                            aria-label="Seções do filtro"
                            className={cn(
                                // fundo do container mais marcado que os botões
                                'flex shrink-0 flex-col gap-2 p-2.5 bg-muted dark:bg-black/25',
                                'border-r border-border/70 sm:w-52',
                                'max-sm:flex-row max-sm:overflow-x-auto max-sm:border-r-0 max-sm:border-b max-sm:w-full',
                            )}
                        >
                            {registro.map(({ id, multiple }) => {
                                const total = arr(valor[id]).length
                                const selecionada = ativa === id
                                const Icone = ICONES_FACETA[id]

                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setAtiva(id)}
                                        aria-current={selecionada || undefined}
                                        title={
                                            multiple
                                                ? `${LABEL[id]} — seleção múltipla`
                                                : LABEL[id]
                                        }
                                        className={cn(
                                            'relative flex items-center justify-between gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm whitespace-nowrap transition-all duration-150 cursor-pointer select-none outline-none',
                                            'focus-visible:ring-2 focus-visible:ring-primary/30',
                                            selecionada
                                                ? 'bg-background font-semibold text-primary shadow-sm border border-border/70 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:bg-primary before:rounded-r-full'
                                                : 'border border-transparent bg-background/55 text-muted-foreground hover:bg-background/85 hover:text-foreground',
                                        )}
                                    >
                                        <div className="flex items-center gap-2 min-w-0 truncate">
                                            {Icone && (
                                                <Icone
                                                    className={cn(
                                                        'size-4 shrink-0 transition-colors',
                                                        selecionada
                                                            ? 'text-primary'
                                                            : 'text-muted-foreground/70',
                                                    )}
                                                />
                                            )}
                                            <span className="truncate">
                                                {LABEL[id]}
                                            </span>
                                        </div>
                                        {total > 0 && (
                                            <Badge
                                                variant="secondary"
                                                className={cn(
                                                    'shrink-0 tabular-nums text-xs px-2 py-0.5 rounded-full font-semibold border-none shadow-none',
                                                    selecionada
                                                        ? 'bg-primary/10 text-primary'
                                                        : 'bg-muted-foreground/10 text-muted-foreground',
                                                )}
                                            >
                                                {total}
                                            </Badge>
                                        )}
                                    </button>
                                )
                            })}
                        </nav>
                        <div className="flex min-w-0 flex-1 flex-col">
                            <div
                                ref={setPainel}
                                className={cn(ALTURA_PAINEL, 'overflow-hidden')}
                            />
                            {temFuncionarios && (
                                <div className="flex items-center justify-between gap-2 border-t border-border/60 bg-muted/30 px-3.5 py-2 text-xs">
                                    <label className="flex items-center gap-2 text-muted-foreground font-medium cursor-pointer select-none hover:text-foreground transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={!!valor.incluirDemitidos}
                                            onChange={e =>
                                                toggleDemitidos(
                                                    e.target.checked,
                                                )
                                            }
                                            className="size-3.5 rounded border-input text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                                        />
                                        <span>
                                            Incluir funcionários demitidos
                                        </span>
                                    </label>
                                    {valor.incluirDemitidos && (
                                        <Badge
                                            variant="outline"
                                            className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-600 border-amber-500/30 font-semibold"
                                        >
                                            Demitidos visíveis
                                        </Badge>
                                    )}
                                </div>
                            )}
                            <footer className="flex items-center justify-between gap-2 border-t border-border/60 bg-muted/20 px-3.5 py-2.5">
                                <div className="flex items-center gap-2 truncate">
                                    <span className="inline-flex size-2 rounded-full bg-emerald-500 shrink-0" />
                                    <span className="truncate text-xs tabular-nums text-muted-foreground font-medium">
                                        <strong className="font-semibold text-foreground">
                                            {dados.totalNoEscopo}
                                        </strong>{' '}
                                        de {dados.totalDaEmpresa} funcionários
                                        incluídos
                                    </span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={limpar}
                                    disabled={!totalSelecionado}
                                    className="h-7 shrink-0 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors font-medium rounded-md"
                                >
                                    <RotateCcwIcon className="size-3.5" />
                                    Limpar seleção
                                </Button>
                            </footer>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </ShellContext.Provider>
    )
}
